import { Box, Typography } from '@mui/material';
import {
  rapgemStoreGemsSrc,
  redemptionVoucherPreviewSrc,
  redemptionXpDividerSrc,
  redemptionXpPanelSrc,
  vipRoomIconsSprite,
  xpCoinSrc,
} from '../../assets/bazzar';
import { formatAbbreviatedXp } from '../../utils/formatAbbreviatedXp';
import { BazaarActionButton, BazaarBackLink, BazaarDialogShell, BazaarRibbonTitle } from './bazaarDialogShared';

/** Figma 8736:26276 — mart confirmation dialog */
const MART_DIALOG_MIN_HEIGHT = 703;
const PURCHASE_DIALOG_MIN_HEIGHT = 520;
const XP_PANEL_WIDTH = 306;
const XP_PANEL_HEIGHT = 162.248;
const VOUCHER_WIDTH = 139.107;
const VOUCHER_HEIGHT = 82;
const titleTextSx = {
  color: '#fff',
  fontWeight: 600,
  fontSize: 16,
  lineHeight: '24px',
  letterSpacing: '0.8px',
  textAlign: 'center',
};

const bodyTextSx = {
  color: '#fff',
  fontWeight: 500,
  fontSize: 14,
  lineHeight: '20px',
  letterSpacing: '0.5px',
  textAlign: 'center',
};

function XpSummaryRow({ label, value, muted = false, valueColor }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        color: '#fff',
        opacity: muted ? 0.7 : 1,
      }}
    >
      <Box component="span" sx={{ fontSize: 14, fontWeight: 500, lineHeight: '20px', letterSpacing: '0.5px' }}>
        {label}
      </Box>
      <Box
        component="span"
        sx={{
          fontSize: 14,
          fontWeight: 500,
          lineHeight: '20px',
          letterSpacing: '0.5px',
          textAlign: 'right',
          color: valueColor || '#fff',
        }}
      >
        {value}
      </Box>
    </Box>
  );
}

function SummaryPanelHeader({ icon, label }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', opacity: 0.7 }}>
      
      <Typography sx={{ ...bodyTextSx, textAlign: 'left' }}>{label}</Typography>
    </Box>
  );
}

function XpCoinIcon({ size = 21 }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '100px',
        overflow: 'hidden',
        flexShrink: 0,
        position: 'relative',
      }}
    >
      <Box
        component="img"
        src={xpCoinSrc}
        alt=""
        sx={{
          position: 'absolute',
          width: '178.09%',
          height: '118.72%',
          maxWidth: 'none',
          left: '-39.86%',
          top: '-8.2%',
        }}
      />
    </Box>
  );
}

function ProductImageIcon({ src, alt, size = 18 }) {
  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      sx={{
        width: size,
        height: size,
        display: 'block',
        objectFit: 'contain',
        flexShrink: 0,
      }}
    />
  );
}

function VipRoomSummaryIcon({ iconType, size = 18 }) {
  const crop = vipRoomIconCrop[iconType] || vipRoomIconCrop.family;

  return (
    <Box
      sx={{
        width: size,
        height: size,
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      <Box
        component="img"
        src={vipRoomIconsSprite}
        alt=""
        sx={{
          width: crop.width,
          height: crop.height,
          maxWidth: 'none',
          display: 'block',
          ml: crop.ml,
          mt: crop.mt,
        }}
      />
    </Box>
  );
}

function PurchaseSummaryIcon({ card }) {
  if (card?.variant === 'store' && card.tone === 'coin' && card.coinImage) {
    return <ProductImageIcon src={card.coinImage} alt="RapCoins" />;
  }

  if ((card?.variant === 'store' && card.tone?.includes('gem')) || card?.passKind === 'gem') {
    return <ProductImageIcon src={rapgemStoreGemsSrc} alt="RapGems" />;
  }

  if ((card?.variant === 'store' && card.tone === 'vip') || card?.passKind === 'vip') {
    return <VipRoomSummaryIcon iconType={card.iconType} />;
  }

  return <XpCoinIcon size={18} />;
}

/** Figma 8736:26380 + XP Summary header — ticket XP panel */
function XpRedemptionPanel({ available, redeeming, after }) {
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: XP_PANEL_WIDTH,
        height: XP_PANEL_HEIGHT,
        mx: 'auto',
        my: 2.2,
      }}
    >
      <Box
        component="img"
        src={redemptionXpPanelSrc}
        alt=""
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          display: 'block',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: '14px',
          px: '14px',
          py: '14px',
        }}
      >
        <SummaryPanelHeader icon={<XpCoinIcon />} label="XP Summary" />
        <XpSummaryRow
          label="Available:"
          value={formatAbbreviatedXp(available)}
          muted
          valueColor="#00d116"
        />
        <XpSummaryRow
          label="Redeeming:"
          value={`- ${formatAbbreviatedXp(redeeming)}`}
          muted
          valueColor="#ff313d"
        />
        <Box component="img" src={redemptionXpDividerSrc} alt="" sx={{ width: '100%', height: 2, display: 'block' }} />
        <XpSummaryRow label="After Redemption:" value={formatAbbreviatedXp(after)} />
      </Box>
    </Box>
  );
}

function PurchaseSummaryPanel({ card, price, originalPrice }) {
  const productLabel = getProductTitle(card);
  const rows = [
    { label: 'Product:', value: productLabel },
    { label: 'Amount:', value: price || '—' },
  ];

  if (originalPrice) {
    rows.push({ label: 'Original Price:', value: originalPrice, muted: true });
    rows.push({ label: 'You Pay:', value: price || '—' });
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: XP_PANEL_WIDTH,
        mx: 'auto',
        my: 2.2,
        px: 2.2,
        py: 2,
        borderRadius: '16px',
        border: '1px solid rgba(255,255,255,0.2)',
        background: 'rgba(0, 0, 0, 0.2)',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.2,
      }}
    >
      <SummaryPanelHeader icon={<PurchaseSummaryIcon card={card} />} label="Purchase Summary" />
      {rows.map((row) => (
        <XpSummaryRow key={row.label} label={row.label} value={row.value} muted={row.muted} />
      ))}
    </Box>
  );
}

const vipRoomIconCrop = {
  family: { width: '384%', height: '250.98%', ml: '-23.25%', mt: '-75.25%' },
  party: { width: '384%', height: '250.98%', ml: '-143.19%', mt: '-75.25%' },
  corporate: { width: '384%', height: '250.98%', ml: '-268.3%', mt: '-75.25%' },
};

function PurchaseProductPreview({ card }) {
  if (card?.variant === 'mart') {
    const voucherImage = card?.imageUrl || redemptionVoucherPreviewSrc;
    return (
      <Box
        component="img"
        src={voucherImage}
        alt={card?.brand || 'Voucher preview'}
        sx={{
          width: VOUCHER_WIDTH,
          height: VOUCHER_HEIGHT,
          display: 'block',
          mx: 'auto',
          mt: '50px',
          objectFit: 'cover',
          borderRadius: '4px',
        }}
      />
    );
  }

  if (card?.variant === 'store' && card.tone === 'coin' && card.coinImage) {
    return (
      <Box
        component="img"
        src={card.coinImage}
        alt={`${card.title} RapCoins`}
        sx={{
          width: card.coinImageWidth || 60,
          height: card.coinImageHeight || 50,
          display: 'block',
          mx: 'auto',
          mt: '50px',
          objectFit: 'contain',
        }}
      />
    );
  }

  if (card?.variant === 'store' && card.tone?.includes('gem')) {
    return (
      <Box
        component="img"
        src={rapgemStoreGemsSrc}
        alt={card.subtitle || 'RapGems'}
        sx={{
          width: 56,
          height: 56,
          mx: 'auto',
          mt: '50px',
          display: 'block',
          objectFit: 'contain',
        }}
      />
    );
  }

  if (card?.variant === 'store' && card.tone === 'vip') {
    const crop = vipRoomIconCrop[card.iconType] || vipRoomIconCrop.family;
    return (
      <Box
        sx={{
          width: 56,
          height: 56,
          mx: 'auto',
          mt: '50px',
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src={vipRoomIconsSprite}
          alt={card.subtitle || 'VIP Room'}
          sx={{
            width: crop.width,
            height: crop.height,
            maxWidth: 'none',
            display: 'block',
            ml: crop.ml,
            mt: crop.mt,
          }}
        />
      </Box>
    );
  }

  return null;
}

function getConfirmCopy(card) {
  if (card?.variant === 'mart') {
    return {
      ribbon: 'Confirm Your Redemption!',
      button: 'Confirm & Redeem',
      minHeight: MART_DIALOG_MIN_HEIGHT,
    };
  }

  return {
    ribbon: 'Confirm Your Purchase!',
    button: 'Confirm & Buy',
    minHeight: PURCHASE_DIALOG_MIN_HEIGHT,
  };
}

function getProductTitle(card) {
  if (!card) return 'Item';

  if (card.variant === 'store' && card.tone === 'coin') {
    return `${card.title} RapCoins`;
  }

  if (card.variant === 'store' && card.gemType === 'mix') {
    return `${card.title} RapGems & ${card.secondaryTitle} RapGems`;
  }

  if (card.variant === 'store' && card.tone?.includes('gem')) {
    return `${card.title} RapGems`;
  }

  if (card.variant === 'store' && card.tone === 'vip') {
    return `${card.title} ${card.subtitle}`;
  }

  return card.title || 'Item';
}

function getProductDescription(card) {
  if (!card) return '';

  if (card.variant === 'mart') {
    return card.subtitle || '';
  }

  if (card.variant === 'passes') {
    if (card.passKind === 'gem') {
      const details = [card.validity, card.swapBenefit, card.boostBenefit].filter(Boolean);
      return details.join(' · ');
    }

    const details = [
      card.validity,
      card.points,
      card.gemsDetail ? `RapGems ${card.gemsDetail}` : '',
      card.roomAccess,
    ].filter(Boolean);
    return details.join(' · ');
  }

  if (card.variant === 'store' && card.tone === 'coin') {
    return `${card.title} RapCoins will be added to your coin wallet instantly after purchase.`;
  }

  if (card.variant === 'store' && card.gemType === 'mix') {
    return `${card.title} Boosts and ${card.secondaryTitle} Swaps will be added to your RapGems wallet.`;
  }

  if (card.variant === 'store' && card.tone?.includes('gem')) {
    return `${card.title} RapGems will be added to your RapGems wallet.`;
  }

  if (card.variant === 'store' && card.tone === 'vip') {
    return `${card.title} ${card.subtitle} will be added to your VIP room wallet.`;
  }

  return card.subtitle || '';
}

export default function ConfirmRedemptionDialog({ open, card, availableXp = 0, confirmLoading = false, onClose, onConfirm }) {
  const isMart = card?.variant === 'mart';
  const redeemingXp = Number(card?.xpValue) || 0;
  const afterXp = Math.max(availableXp - redeemingXp, 0);
  const { ribbon, button, minHeight } = getConfirmCopy(card);
  const productTitle = getProductTitle(card);
  const productDescription = getProductDescription(card);

  return (
    <BazaarDialogShell open={open} onClose={onClose} minHeight={minHeight}>
      <BazaarRibbonTitle>{ribbon}</BazaarRibbonTitle>

      <PurchaseProductPreview card={card} />

      <Typography sx={{ ...titleTextSx, mt: isMart ? 1.6 : 2 }}>
        {productTitle}
      </Typography>

      {productDescription ? (
        <Typography
          sx={{
            ...bodyTextSx,
            mt: 0.6,
            maxWidth: 306,
            mx: 'auto',
            maxHeight: 100,
            overflowY: 'auto',
            px: 0.5,
          }}
        >
          {productDescription}
        </Typography>
      ) : null}

      {isMart ? (
        <XpRedemptionPanel available={availableXp} redeeming={redeemingXp} after={afterXp} />
      ) : (
        <PurchaseSummaryPanel card={card} price={card?.price} originalPrice={card?.originalPrice} />
      )}

      <BazaarActionButton label={button} onClick={onConfirm} loading={confirmLoading} />

      <BazaarBackLink onClick={onClose} />
    </BazaarDialogShell>
  );
}
